import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaBell } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useGroup } from "@/lib/context/GroupContext";
import {
  fetchMyNotifications,
  markAllRead,
  markRead,
  unreadCount,
  type InboxNotification,
} from "@/lib/api/notifications";
import { appNavGhostButtonClass } from "@/components/layout/appNavStyles";

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Ahora";
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `Hace ${diffHours} h`;
  const diffDays = Math.floor(diffHours / 24);
  return `Hace ${diffDays} d`;
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentGroup } = useGroup();
  const groupId = currentGroup?.id;

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<InboxNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!groupId) {
      setItems([]);
      setUnread(0);
      return;
    }
    setLoading(true);
    try {
      const [list, count] = await Promise.all([
        fetchMyNotifications(groupId),
        unreadCount(groupId),
      ]);
      setItems(list);
      setUnread(count);
    } catch (err) {
      console.warn("Failed to load notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    refresh();
    if (!groupId) return;
    const interval = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(interval);
  }, [groupId, refresh]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleItemClick = async (item: InboxNotification) => {
    if (!item.read_at) {
      try {
        await markRead(item.id);
        setUnread((c) => Math.max(0, c - 1));
        setItems((prev) =>
          prev.map((n) =>
            n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n
          )
        );
      } catch (err) {
        console.warn("markRead failed:", err);
      }
    }
    setOpen(false);
    navigate(item.url || "/v2/home");
  };

  const handleMarkAllRead = async () => {
    if (!groupId || unread === 0) return;
    try {
      await markAllRead(groupId);
      setUnread(0);
      setItems((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() }))
      );
    } catch (err) {
      console.warn("markAllRead failed:", err);
    }
  };

  if (!groupId) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(appNavGhostButtonClass, "relative h-9 w-9 shrink-0")}
          aria-label={
            unread > 0
              ? t("notifications.bellWithCount", { count: unread })
              : t("notifications.bell")
          }
        >
          <FaBell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className={cn(
                "absolute top-0.5 right-0.5 min-w-[1.125rem] h-[1.125rem] px-1",
                "flex items-center justify-center rounded-full bg-amber-400 text-[10px] font-bold text-black",
                "ring-2 ring-soccer-primary pointer-events-none"
              )}
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">{t("notifications.title")}</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="text-xs text-soccer-primary hover:underline"
            >
              {t("notifications.markAllRead")}
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading && items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">{t("notifications.empty")}</p>
          ) : (
            <ul className="divide-y">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors",
                      !item.read_at && "bg-soccer-primary/5"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {!item.read_at && (
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-soccer-primary" />
                      )}
                      <div className={cn("min-w-0 flex-1", item.read_at && "pl-4")}>
                        <p className="text-sm font-medium leading-tight truncate">{item.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {item.body}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatRelativeTime(item.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
